import type { User } from '@/api/types'

/**
 * The signed-in session, kept in localStorage so a reload stays signed in.
 * Access tokens live 15 minutes and are refreshed with the 7-day refresh
 * token (see api/client.ts).
 */
export interface StoredSession {
  accessToken: string
  refreshToken: string | null
  user: User
}

const KEY = 'saadhan.session'

type Listener = (session: StoredSession | null) => void
const listeners = new Set<Listener>()

let current: StoredSession | null = read()
let revision = 0

function read(): StoredSession | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as StoredSession
    return typeof value?.accessToken === 'string' && Number.isInteger(value?.user?.id) ? value : null
  } catch {
    return null
  }
}

export const session = {
  get: () => current,
  revision: () => revision,
  set(next: StoredSession | null) {
    if (current?.user.id !== next?.user.id) {
      revision++
      // Old unscoped preferences have no trustworthy owner. Never assign them
      // to the next account, or carry a guest's pending charge into a login.
      try {
        localStorage.removeItem('saadhan.favourites')
        localStorage.removeItem('saadhan.watches')
        localStorage.removeItem('saadhan.favourites.guest')
        localStorage.removeItem('saadhan.watches.guest')
        sessionStorage.removeItem('saadhan.starting')
      } catch { /* storage may be unavailable */ }
    }
    current = next
    try {
      if (next) localStorage.setItem(KEY, JSON.stringify(next))
      else {
        localStorage.removeItem(KEY)
      }
    } catch {
      /* private mode: the session lasts until reload */
    }
    listeners.forEach((l) => l(next))
  },
  updateAccessToken(token: string) {
    if (current) session.set({ ...current, accessToken: token })
  },
  subscribe(listener: Listener) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

// Logout and account changes in another tab must use the same cleanup path.
window.addEventListener('storage', (event) => {
  if (event.key === KEY || event.key === null) session.set(read())
})
